import './outreach_status_card.css';

import { Check, Megaphone, Pause, X } from 'lucide-react';
import { type ReactNode } from 'react';

import {
    OUTREACH_CARD_HEADINGS,
    OUTREACH_CARD_STATES,
    type OutreachCardHeading,
    type OutreachCardStatus,
} from '@apps/web/features/phoebe_chat_v3/model/outreach_card_status';
import { cn } from '@apps/web/lib/utils';

export function OutreachStatusCard({
    heading,
    status,
    viewHref,
}: Readonly<{
    heading: OutreachCardHeading;
    status?: OutreachCardStatus;
    viewHref: string;
}>): ReactNode {
    const state =
        status === undefined ? undefined : OUTREACH_CARD_STATES[status];
    const running = state?.indicator === 'pulse';
    const title = OUTREACH_CARD_HEADINGS[heading];
    return (
        <article
            aria-label={title}
            className="bg-card text-foreground min-w-0 rounded-xl border p-4 shadow-[0_2px_6px_rgba(0,0,0,0.02)] sm:px-5"
        >
            <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-2">
                    <Megaphone aria-hidden="true" className="size-4 shrink-0" />
                    <h3 className="text-v3-chat font-semibold">{title}</h3>
                </div>
                {status && (
                    <span
                        role="status"
                        className={cn(
                            'inline-flex shrink-0 items-center gap-1.5 text-v3-chat-meta',
                            running
                                ? 'text-agent-action'
                                : 'text-muted-foreground',
                        )}
                    >
                        {running ? (
                            <span
                                aria-hidden="true"
                                className="outreach-running-signal relative inline-grid size-3 place-items-center"
                            >
                                <span className="size-[5px] rounded-full bg-current" />
                            </span>
                        ) : state?.indicator === 'check' ? (
                            <Check aria-hidden="true" className="size-3.5" />
                        ) : state?.indicator === 'pause' ? (
                            <Pause aria-hidden="true" className="size-3" />
                        ) : state?.indicator === 'cancel' ? (
                            <X aria-hidden="true" className="size-3" />
                        ) : null}
                        {state?.label}
                    </span>
                )}
            </div>
            <div className="mt-3.5 flex justify-end">
                <a
                    href={viewHref}
                    className="focus-visible:ring-ring text-v3-chat inline-flex min-h-8 items-center justify-center rounded-md bg-zinc-700 px-3 py-1.5 font-medium text-white transition-colors hover:bg-zinc-800 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none"
                >
                    View outreach
                </a>
            </div>
        </article>
    );
}
